/**
 * `<MapVisibilitySetting/>` — B6.2, the per-team Map visibility choice.
 *
 * Issue #761 (Phase 2). The lead picks how much of the map members see. Two
 * plain options (§A8):
 *
 *   - **Everyone on the team sees the whole map** (recommended, the default) —
 *     collaborative knowledge work where shared sight of the plan is the point.
 *   - **Members see only their own branch** — an OPTIONAL privacy restriction:
 *     the lead always sees everything; a member sees their part and the outcome
 *     above it.
 *
 * This is the **same per-team component mechanism** the scheduling side and B6.3
 * use — there is no bespoke visibility store:
 *
 *   - `useTeamFeatures(teamId)` reads `GET /api/teams/{id}/features`; we read the
 *     single `task_member_branch_only` entry's `enabled` flag (`true` = the
 *     restriction is on = "own branch"; `false` = "whole map", the default).
 *   - `useToggleComponent(teamId)` PATCHes `…/features/task_member_branch_only`;
 *     we reuse it verbatim. Its `onSuccess` writes the updated registry straight
 *     into the query cache, so the choice reflects immediately. **The server is
 *     the real enforcement** — `GET /tasks/nodes` already filters a non-lead
 *     member to their own branch when this key is on.
 *
 * Faithful to `B6-2-visibility-setting.svg`: a white card titled "Who can see
 * the map" with a thin rule, two radio options (the selected one tinted with an
 * indigo ring + a "recommended" pill on the whole-map option), and a
 * cancel / save button pair. A small "who can see this" privacy line states the
 * posture plainly (§A8, the trustable pillar made visible).
 *
 * Lead-only (§B6.2 is *lead/PI*): when `canManage` is false the options render
 * read-only (no draft edits, no save) and a one-line note explains the current
 * posture instead of offering a control the API would reject.
 *
 * Brand firewall (§A9, banlist): factual copy, **no tier/plan language**, the
 * restriction is framed as a privacy aid, never a scorecard or surveillance
 * lever. Isolation: depends only on the generic features hook + UI primitives;
 * no scheduler imports.
 *
 * Editorial revamp (#1043, Wave 4, B6.2): theme-aware via `useMapTheme()`. The
 * `task_editorial_ui` flag stays OFF for every team (default LEGACY) until the
 * whole revamp lands, so:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint card — the
 *     indigo-ringed selected option + the green "recommended" pill.
 *   - EDITORIAL (flag on): the wireframe `V_B62` — two option `.t-card`s; the
 *     selected one carries a `1.5px c.ink` border + a mono `RECOMMENDED ✓`
 *     ISSUE tag; cancel/save `.t-btn`s; the italic "shows on every node detail
 *     drawer · no surprises" footer.
 * RE-SKIN, not rebuild: every `data-testid`, copy, and behaviour is preserved.
 */

import { useEffect, useState } from "react";

import { useTeamFeatures, useToggleComponent } from "../../api/useTeamFeature";
import { Button, ErrorState, Skeleton } from "../../components/ui";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";

/** The registry key this screen toggles (matches `backend/.../registry.py`). */
export const VISIBILITY_KEY = "task_member_branch_only";

const INK = "#0C2A2A";
const MUTED = "#9CA3AF";
const SUBTLE = "#5C7878";
const SELECTED_BORDER = "#4F46E5";
const SELECTED_FILL = "#EEF2FF";

/** The two visibility postures, keyed by the `enabled` value of the key. */
type Choice = "whole_map" | "own_branch";

export interface MapVisibilitySettingProps {
  teamId: string | undefined;
  /** When false, the choice renders read-only (non-lead safety). */
  canManage: boolean;
  /** Called after a successful save or a cancel, so the host can close. */
  onClose?: () => void;
}

export function MapVisibilitySetting({ teamId, canManage, onClose }: MapVisibilitySettingProps) {
  const featuresQuery = useTeamFeatures(teamId);
  const toggle = useToggleComponent(teamId);
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // `task_member_branch_only` ON  → members see only their own branch.
  // OFF (default)                 → everyone sees the whole map.
  const restrictionOn =
    featuresQuery.data?.features.find((f) => f.key === VISIBILITY_KEY)?.enabled ?? false;
  const serverChoice: Choice = restrictionOn ? "own_branch" : "whole_map";

  // A local draft so the radio is responsive before save; the server is the
  // source of truth, so we re-sync the draft whenever the server value lands or
  // changes (e.g. after a successful save writes the cache).
  const [draft, setDraft] = useState<Choice>(serverChoice);
  useEffect(() => {
    setDraft(serverChoice);
  }, [serverChoice]);

  if (featuresQuery.isPending) {
    return (
      <div className="flex flex-col gap-3" data-testid="map-visibility-loading">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (featuresQuery.isError) {
    return (
      <ErrorState
        title="Couldn’t load the visibility setting"
        message="Refresh the page; if it keeps happening, check your connection and try again."
      />
    );
  }

  const dirty = draft !== serverChoice;

  const handleSave = () => {
    if (!canManage || !dirty) return;
    // ON when the lead chose "own branch"; OFF for the shared whole-map default.
    toggle.mutate(
      { componentKey: VISIBILITY_KEY, enabled: draft === "own_branch", cascade: false },
      { onSuccess: () => onClose?.() },
    );
  };

  const handleCancel = () => {
    setDraft(serverChoice);
    onClose?.();
  };

  return (
    <div
      data-testid="map-visibility-setting"
      style={{ fontFamily: editorial ? theme.fonts.sans : undefined }}
    >
      <div
        className="overflow-hidden bg-white"
        style={
          editorial
            ? {
                border: `1px solid ${c.rule}`,
                borderRadius: theme.radii.lg,
                backgroundColor: c.cardBg,
                boxShadow: "0 12px 36px rgba(0,0,0,0.08)",
              }
            : {
                borderRadius: 12,
                border: "1px solid #CFE0D8",
                boxShadow: "0 2px 8px rgba(31,122,110,0.12)",
              }
        }
      >
        <div className="px-6 pt-5">
          {editorial ? (
            <span
              className="text-[10px] uppercase"
              style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.1em" }}
            >
              Who can see the map
            </span>
          ) : (
            <h3 className="text-sm font-bold" style={{ color: INK }}>
              Who can see the map
            </h3>
          )}
        </div>
        <div
          className="mt-3 border-t px-6 py-5"
          style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}
        >
          <fieldset className="flex flex-col gap-3" disabled={!canManage}>
            <legend className="sr-only">Who can see the map</legend>

            <VisibilityOption
              theme={theme}
              testId="visibility-option-whole_map"
              selected={draft === "whole_map"}
              readOnly={!canManage}
              title="Everyone on the team sees the whole map"
              titleAccessory={<RecommendedPill theme={theme} />}
              description="Best for shared, collaborative work."
              onSelect={() => canManage && setDraft("whole_map")}
            />

            <VisibilityOption
              theme={theme}
              testId="visibility-option-own_branch"
              selected={draft === "own_branch"}
              readOnly={!canManage}
              title="Members see only their own branch"
              description="The lead sees everything; members see their part and the outcome above it."
              onSelect={() => canManage && setDraft("own_branch")}
            />
          </fieldset>

          {/* The visible "who can see this" privacy line (§A8, trustable
              pillar). States the live posture plainly, in factual copy. */}
          <p
            data-testid="visibility-who-can-see"
            className="mt-4 text-[11px]"
            style={{ color: editorial ? c.muted : SUBTLE }}
          >
            {restrictionOn
              ? "Right now: each member sees only their own branch and the outcome above it; the lead sees the whole map. We never show one person’s activity to another as a count or a ranking."
              : "Right now: everyone on the team sees the whole map. We never show one person’s activity to another as a count or a ranking."}
          </p>
        </div>

        {/* Footer: cancel / save for a lead; an explanatory line for a member. */}
        <div
          className="flex items-center justify-end gap-2 border-t px-6 py-3"
          style={{ borderColor: editorial ? c.rule : "#F3F4F6" }}
        >
          {canManage ? (
            editorial ? (
              <>
                <button
                  type="button"
                  data-testid="visibility-cancel"
                  onClick={handleCancel}
                  disabled={toggle.isPending}
                  className="rounded-md border px-4 py-1.5 text-[12px] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ borderColor: c.rule2, color: c.ink, backgroundColor: c.cardBg }}
                >
                  cancel
                </button>
                <button
                  type="button"
                  data-testid="visibility-save"
                  onClick={handleSave}
                  disabled={!dirty || toggle.isPending}
                  className="rounded-md px-4 py-1.5 text-[12px] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: c.ink, color: c.canvas }}
                >
                  save
                </button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={toggle.isPending}
                  data-testid="visibility-cancel"
                >
                  cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || toggle.isPending}
                  data-testid="visibility-save"
                >
                  save
                </Button>
              </>
            )
          ) : (
            <p
              className="text-[11px] italic"
              style={{ color: editorial ? c.muted : MUTED }}
              data-testid="visibility-readonly-note"
            >
              Only the team lead can change who sees the map.
            </p>
          )}
        </div>
      </div>

      {/* Editorial footer (`V_B62`): the calm "no surprises" privacy line.
          Legacy has no equivalent line outside the card, so it renders only in
          editorial mode — legacy stays byte-identical. */}
      {editorial && (
        <p
          data-testid="visibility-no-surprises"
          className="mt-5 text-center text-[11.5px] italic"
          style={{ color: c.muted }}
        >
          Whatever you pick shows on every node detail drawer ·{" "}
          <span style={{ color: c.ink }}>no surprises.</span>
        </p>
      )}
    </div>
  );
}

interface VisibilityOptionProps {
  theme: MapTheme;
  testId: string;
  selected: boolean;
  readOnly: boolean;
  title: string;
  titleAccessory?: React.ReactNode;
  description: string;
  onSelect: () => void;
}

/**
 * One radio-style choice row. Renders as a real radio `<input>` (keyboard +
 * screen-reader accessible). Legacy styles it as the gallery's soft-mint option
 * (a filled indigo dot when selected, the row tinted indigo). Editorial styles
 * it as a `.t-card`: selected = a `1.5px c.ink` border, unselected = a hairline
 * border; the radio dot is hidden in editorial (the border is the affordance).
 */
function VisibilityOption({
  theme,
  testId,
  selected,
  readOnly,
  title,
  titleAccessory,
  description,
  onSelect,
}: VisibilityOptionProps) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  return (
    <label
      data-testid={testId}
      data-selected={selected}
      className={`flex items-start gap-3 border px-4 py-3 ${editorial ? "" : "rounded-lg"} ${
        readOnly ? "cursor-default" : "cursor-pointer"
      }`}
      style={
        editorial
          ? {
              backgroundColor: c.cardBg,
              borderColor: selected ? c.ink : c.rule,
              borderWidth: selected ? 1.5 : 1,
              borderRadius: theme.radii.lg,
            }
          : {
              backgroundColor: selected ? SELECTED_FILL : "#F7F8FA",
              borderColor: selected ? SELECTED_BORDER : "#E3E6EC",
            }
      }
    >
      <input
        type="radio"
        name="map-visibility"
        className="sr-only"
        checked={selected}
        disabled={readOnly}
        onChange={onSelect}
      />
      {/* Legacy: the visible radio dot. Editorial: the `1.5px ink` border is
          the selection affordance, so the dot is hidden (the wireframe option
          card has no dot). */}
      {!editorial && (
        <span
          aria-hidden
          className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full"
          style={{
            border: selected ? "none" : "1.5px solid #9CA3AF",
            backgroundColor: selected ? SELECTED_BORDER : "transparent",
          }}
        >
          {selected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className={`flex items-center gap-2 ${editorial ? "justify-between" : ""}`}>
          <span
            className="text-sm"
            style={
              editorial
                ? { color: c.ink, fontWeight: 500 }
                : { fontSize: 12, color: INK, fontWeight: selected ? 700 : 500 }
            }
          >
            {title}
          </span>
          {titleAccessory}
        </span>
        <span
          className="mt-1.5 block text-[12.5px]"
          style={{
            color: editorial ? c.muted : selected ? SUBTLE : MUTED,
            fontSize: editorial ? undefined : 11,
          }}
        >
          {description}
        </span>
      </span>
    </label>
  );
}

/**
 * The "recommended" tag on the whole-map (default) option. Legacy: a green
 * pill. Editorial: a mono `RECOMMENDED ✓` ISSUE tag (`V_B62`).
 */
function RecommendedPill({ theme }: { theme: MapTheme }) {
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  if (editorial) {
    return (
      <span
        data-testid="visibility-recommended"
        className="shrink-0 text-[10px] uppercase"
        style={{ fontFamily: theme.fonts.mono, color: c.muted, letterSpacing: "0.08em" }}
      >
        Recommended ✓
      </span>
    );
  }
  return (
    <span
      className="rounded px-1.5 py-0.5 text-[9px] font-medium"
      style={{ backgroundColor: "#DCFCE7", color: "#059669" }}
    >
      recommended
    </span>
  );
}
